import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Award, Compass, Landmark, Lock, ShieldCheck, Star, Timer, TrendingDown, Tractor } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { usePageTitle } from "@/lib/seo";
import { SOLUTIONS } from "@/data/solutions";
import { LeadForm } from "@/components/LeadForm";
import { Marquee } from "@/components/Marquee";
import { Reveal } from "@/components/Reveal";
import { CTASection } from "@/components/CTASection";
import { API } from "@/lib/api";

const ICONS = { TrendingDown, Tractor, Compass, Landmark, ShieldCheck };

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.2, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const Hero = () => {
  const { t } = useLanguage();
  const { scrollY } = useScroll();
  const yBg = useTransform(scrollY, [0, 900], [0, 180]);

  return (
    <section className="relative flex min-h-screen items-center overflow-hidden pt-24" data-testid="hero-section">
      <motion.div style={{ y: yBg }} className="absolute inset-0 scale-110">
        <img
          src="https://images.unsplash.com/photo-1595024600400-2a49b9fce270?q=80&w=2000&auto=format&fit=crop"
          alt="Câmp agricol la apus"
          className="h-full w-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/60 to-ink/20" />
      <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-transparent to-ink/30" />

      <div className="relative z-10 mx-auto grid w-full max-w-7xl gap-12 px-6 pb-24 pt-16 md:px-10 lg:grid-cols-12 lg:gap-8">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15 }}
            data-testid="hero-overline"
            className="flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-bronze md:text-sm"
          >
            <span className="h-px w-10 bg-bronze" />
            {t.hero.overline}
          </motion.p>
          <h1 className="mt-8 font-serif text-5xl leading-[0.95] tracking-tight text-ivory md:text-7xl lg:text-[5.5rem]" data-testid="hero-heading">
            <MaskedLine delay={0.3}>{t.hero.line1}</MaskedLine>
            <MaskedLine delay={0.45}>
              {t.hero.line2Pre} <em className="italic text-bronze">{t.hero.line2Accent}</em> {t.hero.line2Post}
            </MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.75, ease: [0.22, 1, 0.36, 1] }}
            className="mt-8 max-w-xl text-base leading-relaxed text-ivory/70 md:text-lg"
            data-testid="hero-subheading"
          >
            {t.hero.sub}
          </motion.p>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.1 }}
            className="mt-14 hidden items-center gap-3 text-xs uppercase tracking-[0.3em] text-ivory/50 lg:flex"
          >
            <motion.span
              animate={{ y: [0, 8, 0] }}
              transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
              className="h-8 w-px bg-bronze/60"
            />
            {t.hero.scroll}
          </motion.div>
        </div>

        <div className="lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="border border-line bg-ivory p-7 shadow-2xl shadow-black/40 md:p-9"
          >
            <LeadForm source="hero" />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const Manifesto = () => {
  const { t } = useLanguage();
  return (
    <section className="relative overflow-hidden" data-testid="manifesto-section">
      <img
        src="https://images.unsplash.com/photo-1500382017468-9049fed747ef?q=80&w=1600&auto=format&fit=crop"
        alt=""
        aria-hidden="true"
        className="absolute inset-0 h-full w-full object-cover opacity-[0.10]"
      />
      <div className="relative mx-auto max-w-7xl px-6 py-24 md:px-10 md:py-36">
      <Reveal>
        <p className="text-xs uppercase tracking-[0.25em] text-bronze md:text-sm">{t.manifesto.overline}</p>
        <h2 className="mt-4 max-w-4xl font-serif text-4xl leading-none tracking-tight text-ink md:text-5xl lg:text-6xl">
          {t.manifesto.heading}
        </h2>
      </Reveal>
      <div className="mt-16 space-y-16 md:mt-24 md:space-y-24">
        {t.manifesto.chapters.map((c, i) => (
          <Reveal key={c.title} delay={0.05}>
            <div className={`grid gap-8 md:grid-cols-12 md:items-start ${i % 2 === 1 ? "md:text-right" : ""}`}>
              <div className={`md:col-span-3 ${i % 2 === 1 ? "md:order-2" : ""}`}>
                <span className="font-serif text-7xl leading-none text-bronze/25 md:text-8xl">
                  0{i + 1}
                </span>
              </div>
              <div className={`md:col-span-6 ${i % 2 === 1 ? "md:order-1" : ""}`}>
                <h3 className="font-serif text-3xl text-ink md:text-4xl">{c.title}</h3>
                <p className="mt-4 text-base leading-relaxed text-stone md:text-lg">{c.text}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
      </div>
    </section>
  );
};

const Statement = () => {
  const { t } = useLanguage();
  return (
    <section className="mx-auto max-w-5xl px-6 py-20 md:px-10 md:py-28" data-testid="statement-section">
      <Reveal>
        <p className="flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-bronze md:text-sm">
          <span className="h-px w-10 bg-bronze" />
          {t.statement.overline}
        </p>
        <p className="mt-8 font-serif text-3xl leading-snug text-ink md:text-4xl lg:text-[2.6rem]">
          {t.statement.text}
        </p>
      </Reveal>
    </section>
  );
};

const ProtectSystem = () => {
  const { t } = useLanguage();
  return (
    <section className="border-t border-coal bg-ink" data-testid="protect-system-section">
      <div className="mx-auto grid max-w-7xl gap-14 px-6 py-24 md:px-10 md:py-32 lg:grid-cols-12 lg:gap-10">
        <Reveal className="lg:col-span-5">
          <div className="lg:sticky lg:top-32">
            <p className="flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-bronze md:text-sm">
              <span className="h-px w-10 bg-bronze" />
              {t.protect.overline}
            </p>
            <h2 className="mt-6 font-serif text-4xl leading-none tracking-tight text-ivory md:text-5xl lg:text-6xl">
              {t.protect.heading}
            </h2>
            <p className="mt-6 max-w-md text-base leading-relaxed text-ivory/60">{t.protect.sub}</p>
            <Link
              data-testid="protect-more-link"
              to="/metoda"
              className="group mt-8 inline-flex items-center gap-3 border border-ivory/40 px-8 py-4 text-sm font-medium uppercase tracking-widest text-ivory transition-colors duration-300 hover:bg-ivory hover:text-ink"
            >
              {t.protect.more}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </Reveal>
        <div className="lg:col-span-7">
          {t.protect.steps.map((s, i) => (
            <Reveal key={i} delay={i * 0.05}>
              <div data-testid={`protect-step-${i}`} className="group flex items-baseline gap-6 border-b border-coal py-6 transition-colors duration-300 first:border-t hover:bg-white/[0.02]">
                <span className="w-10 shrink-0 font-serif text-4xl text-bronze">{s.letter}</span>
                <div className="flex-1">
                  <h3 className="font-serif text-2xl text-ivory md:text-3xl">{s.title}</h3>
                  <p className="mt-1 text-sm leading-relaxed text-ivory/60 md:text-base">{s.desc}</p>
                </div>
                <span className="hidden text-xs uppercase tracking-widest text-ivory/25 sm:block">0{i + 1}</span>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const Reviews = () => {
  const { t } = useLanguage();
  return (
    <section className="border-t border-line" data-testid="reviews-section">
      <div className="mx-auto max-w-7xl px-6 py-24 md:px-10 md:py-32">
        <Reveal>
          <p className="flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-bronze md:text-sm">
            <span className="h-px w-10 bg-bronze" />
            {t.reviews.overline}
          </p>
          <h2 className="mt-4 max-w-2xl font-serif text-4xl leading-none tracking-tight text-ink md:text-5xl">
            {t.reviews.heading}
          </h2>
        </Reveal>
        <div className="mt-14 grid gap-6 md:grid-cols-3">
          {t.reviews.items.map((r, i) => (
            <Reveal key={i} delay={i * 0.1}>
              <figure data-testid={`review-card-${i}`} className="flex h-full flex-col border border-line bg-white p-8 transition-colors duration-300 hover:border-bronze/50">
                <div className="flex gap-1">
                  {[0, 1, 2, 3, 4].map((s) => (
                    <Star key={s} className="h-4 w-4 fill-bronze text-bronze" />
                  ))}
                </div>
                <blockquote className="mt-5 flex-1 font-serif text-xl leading-snug text-ink md:text-2xl">
                  „{r.text}"
                </blockquote>
                <figcaption className="mt-6 border-t border-line pt-4 text-xs uppercase tracking-[0.15em] text-stone">
                  {r.author}
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const SolutionsGrid = () => {
  const { t, lang } = useLanguage();
  return (
    <section className="border-t border-line" data-testid="solutions-section">
      <div className="mx-auto max-w-7xl px-6 py-24 md:px-10 md:py-36">
        <Reveal>
          <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-bronze md:text-sm">{t.solutionsSection.overline}</p>
              <h2 className="mt-4 max-w-2xl font-serif text-4xl leading-none tracking-tight text-ink md:text-5xl lg:text-6xl">
                {t.solutionsSection.heading}
              </h2>
            </div>
            <p className="max-w-md text-base leading-relaxed text-stone">{t.solutionsSection.sub}</p>
          </div>
        </Reveal>
        <div className="mt-16 grid gap-5 md:grid-cols-2 lg:grid-cols-6">
          {SOLUTIONS.map((s, i) => {
            const Icon = ICONS[s.icon];
            const span = i === 0 ? "lg:col-span-4" : i === 1 ? "lg:col-span-2" : "lg:col-span-2";
            return (
              <Reveal key={s.slug} delay={i * 0.07} className={span}>
                <Link
                  data-testid={`solution-card-${s.slug}`}
                  to={`/solutii/${s.slug}`}
                  className="group flex h-full flex-col border border-line bg-sand transition-colors duration-500 hover:border-bronze/40"
                >
                  <div className="relative h-52 overflow-hidden">
                    <img
                      src={s.image}
                      alt={s[lang].name}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-ink/10 to-transparent" />
                    <span className="absolute left-5 top-5 flex h-11 w-11 items-center justify-center border border-bronze/40 bg-ivory/80 backdrop-blur">
                      <Icon className="h-5 w-5 text-bronze" />
                    </span>
                    <span className="absolute right-5 top-5 font-serif text-4xl text-ivory/50">0{i + 1}</span>
                  </div>
                  <div className="flex flex-1 flex-col gap-4 p-7">
                    <h3 className="font-serif text-2xl text-ink transition-colors duration-300 group-hover:text-bronze md:text-3xl">
                      {s[lang].name}
                    </h3>
                    <p className="flex-1 text-sm leading-relaxed text-stone">{s[lang].short}</p>
                    <span className="flex items-center gap-2 text-xs font-medium uppercase tracking-widest text-bronze">
                      {t.solutionsSection.cta}
                      <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const TrustStrip = () => {
  const { t } = useLanguage();
  const icons = [Award, Timer, Lock];
  return (
    <section className="mx-auto max-w-7xl px-6 py-24 md:px-10 md:py-32" data-testid="trust-section">
      <div className="grid gap-10 md:grid-cols-3 md:gap-8">
        {t.trust.map((item, i) => {
          const Icon = icons[i];
          return (
            <Reveal key={item.title} delay={i * 0.1}>
              <div className="flex gap-5 border-t border-line pt-8">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center border border-bronze/30 bg-sand">
                  <Icon className="h-5 w-5 text-bronze" />
                </span>
                <div>
                  <h3 className="font-serif text-2xl text-ink">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-stone">{item.text}</p>
                </div>
              </div>
            </Reveal>
          );
        })}
      </div>
    </section>
  );
};

const BlogPreview = () => {
  const { t, lang } = useLanguage();
  const [articles, setArticles] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/articles`, { params: { lang } })
      .then((r) => setArticles(r.data.slice(0, 3)))
      .catch(() => setArticles([]));
  }, [lang]);

  if (articles.length === 0) return null;

  return (
    <section className="border-t border-line" data-testid="blog-preview-section">
      <div className="mx-auto max-w-7xl px-6 py-24 md:px-10 md:py-32">
        <Reveal>
          <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-bronze md:text-sm">{t.blogSection.overline}</p>
              <h2 className="mt-4 max-w-2xl font-serif text-4xl leading-none tracking-tight text-ink md:text-5xl">
                {t.blogSection.heading}
              </h2>
            </div>
            <Link
              data-testid="blog-preview-all-link"
              to="/blog"
              className="group flex items-center gap-2 text-xs font-medium uppercase tracking-widest text-bronze"
            >
              {t.blogSection.cta}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </Reveal>
        <div className="mt-14 grid gap-8 md:grid-cols-3">
          {articles.map((a, i) => (
            <Reveal key={a.slug} delay={i * 0.1}>
              <Link data-testid={`blog-preview-card-${a.slug}`} to={`/blog/${a.slug}`} className="group flex h-full flex-col gap-5">
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={a.image}
                    alt={a.title}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <span className="absolute left-4 top-4 bg-ivory/85 px-3 py-1 text-[10px] uppercase tracking-widest text-bronze backdrop-blur">
                    {a.category}
                  </span>
                </div>
                <div className="flex flex-1 flex-col gap-3">
                  <p className="text-xs text-stone/60">{a.date} · {a.read_minutes} {t.blogSection.minRead}</p>
                  <h3 className="font-serif text-2xl leading-tight text-ink transition-colors duration-300 group-hover:text-bronze">
                    {a.title}
                  </h3>
                  <p className="line-clamp-2 text-sm leading-relaxed text-stone">{a.excerpt}</p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export default function Home() {
  const { t } = useLanguage();
  usePageTitle(t.meta.home);
  return (
    <div data-testid="home-page">
      <Hero />
      <Marquee />
      <Statement />
      <Manifesto />
      <ProtectSystem />
      <SolutionsGrid />
      <TrustStrip />
      <Reviews />
      <CTASection />
      <BlogPreview />
    </div>
  );
}
